// src/pages/TaskDefinitionsPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Card, CardContent, Avatar, IconButton,
  TextField, InputAdornment, CircularProgress, Tooltip, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton, Pagination, Skeleton, Divider,
  Dialog, DialogContent, DialogTitle, DialogActions, Select, MenuItem,
  FormControl, Button,
} from '@mui/material';
import {
  Search, GridView, ViewList, Refresh, Close, CheckCircle,
  Cancel, FiberManualRecord, Extension, Lock, Public,
  LocationOn, Image as ImageIcon, TextFields, Numbers,
  ArrowDropDown, Folder, Add, Edit, Delete, Warning,
} from '@mui/icons-material';
import { selectToken } from '../store/authSlice';

const API_BASE = 'https://gsm-monitoring-api.govirtual.cloud/api/v1';

// ── Field type icon ───────────────────────────────────────────────────────────
function FieldTypeIcon({ type, size = 13 }) {
  const map = {
    text:      <TextFields sx={{ fontSize: size, color: '#2dccd2' }} />,
    number:    <Numbers    sx={{ fontSize: size, color: '#0a9e74' }} />,
    select:    <ArrowDropDown sx={{ fontSize: size, color: '#7b1fa2' }} />,
    textarea:  <TextFields sx={{ fontSize: size, color: '#4a6a85' }} />,
    images:    <ImageIcon  sx={{ fontSize: size, color: '#c06000' }} />,
    signature: <Extension  sx={{ fontSize: size, color: '#d32f2f' }} />,
  };
  return map[type] || <Extension sx={{ fontSize: size, color: '#9aacb8' }} />;
}

function countFields(task) {
  if (task.steps?.length) return task.steps.reduce((a, s) => a + (s.fields?.length || 0), 0);
  return task.fields?.length || 0;
}

// ── Delete confirm dialog ─────────────────────────────────────────────────────
function DeleteConfirmDialog({ task, open, onClose, onConfirm, deleting }) {
  if (!task) return null;
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '16px', p: 0.5, maxWidth: 420 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fff3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Warning sx={{ fontSize: 20, color: '#ef4444' }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f2137' }}>
          Delete "{task.label}"?
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography sx={{ fontSize: '0.88rem', color: '#4a6a85' }}>
          This will permanently delete this task definition and all its configuration. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={deleting}
          sx={{ borderRadius: '10px', borderColor: '#e5e7eb', color: '#4a6a85', textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Delete />}
          sx={{ borderRadius: '10px', background: '#ef4444', textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { background: '#dc2626' } }}>
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Task detail modal ─────────────────────────────────────────────────────────
function TaskDetailModal({ task, open, onClose, onEdit, onDelete }) {
  if (!task) return null;

  const allFields = task.steps?.length
    ? task.steps.flatMap((s) => (s.fields || []).map((f) => ({ ...f, _step: s.label })))
    : (task.fields || []);

  const isMultiStep = !!task.steps?.length;
  const accentColor = task.color || '#2dccd2';

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '98vw', md: '640px' },
          maxHeight: '90vh', borderRadius: '20px',
          background: '#fff',
          boxShadow: '0 24px 64px rgba(15,33,55,0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5, flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.1)}, ${alpha('#2dccd2', 0.04)})`,
          borderBottom: '1px solid rgba(16,117,186,0.1)',
          position: 'relative',
        }}>
          <Box sx={{
            position: 'absolute', right: -20, top: -20, width: 140, height: 140,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(accentColor, 0.1)}, transparent 70%)`,
            pointerEvents: 'none',
          }} />
          <IconButton onClick={onClose} size="small"
            sx={{ position: 'absolute', top: 12, right: 12, color: '#4a6a85' }}>
            <Close fontSize="small" />
          </IconButton>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              width: 54, height: 54, borderRadius: '14px', flexShrink: 0,
              background: task.bg || '#e6f2ff',
              border: `2px solid ${alpha(accentColor, 0.25)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.6rem',
            }}>
              {task.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f2137', lineHeight: 1.2, mb: 0.5 }}>
                {task.label}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                <Chip size="small" label={task.id}
                  sx={{ height: 20, fontSize: '0.66rem', fontFamily: 'monospace', background: '#f0f4f8', color: '#4a6a85', border: '1px solid rgba(16,117,186,0.12)' }} />
                <Chip size="small"
                  icon={task.active ? <CheckCircle sx={{ fontSize: '11px !important' }} /> : <Cancel sx={{ fontSize: '11px !important' }} />}
                  label={task.active ? 'Active' : 'Inactive'}
                  sx={{
                    height: 20, fontSize: '0.66rem', fontWeight: 700,
                    background: task.active ? '#e6f9f4' : '#f5f5f5',
                    color: task.active ? '#0a9e74' : '#9e9e9e',
                    border: `1px solid ${task.active ? '#b3ead9' : '#e0e0e0'}`,
                  }} />
                {isMultiStep && (
                  <Chip size="small" label={`${task.steps.length} steps`}
                    sx={{ height: 20, fontSize: '0.66rem', background: '#e6f2ff', color: '#2dccd2', border: '1px solid #99ccf5' }} />
                )}
                {task.getLocation && (
                  <Chip size="small" icon={<LocationOn sx={{ fontSize: '11px !important' }} />} label="GPS"
                    sx={{ height: 20, fontSize: '0.66rem', background: '#f5e6ff', color: '#7b1fa2', border: '1px solid #d9b3f5' }} />
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Stack spacing={2.5}>
            {(task.confirmTitle || task.confirmBody) && (
              <Box sx={{ background: '#f5f8fc', borderRadius: '12px', p: 2, border: '1px solid rgba(16,117,186,0.1)' }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2dccd2', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                  Confirmation Prompt
                </Typography>
                {task.confirmTitle && <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f2137', mb: 0.5 }}>{task.confirmTitle}</Typography>}
                {task.confirmBody  && <Typography sx={{ fontSize: '0.8rem', color: '#4a6a85' }}>{task.confirmBody}</Typography>}
              </Box>
            )}

            {task.access && (
              <Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2dccd2', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                  Access Control
                </Typography>
                <Stack spacing={0.75}>
                  {(['projects','roles','users']).map((key) => {
                    const items = task.access[key];
                    if (!items?.length) return null;
                    return (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85', minWidth: 60, mt: 0.3, textTransform: 'capitalize' }}>{key}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {items.map((item, i) => (
                            <Chip key={i} size="small"
                              label={typeof item === 'object' ? (item.title || item.label || item._id) : item}
                              sx={{ height: 20, fontSize: '0.66rem', background: '#f0f4f8', color: '#0f2137' }} />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Divider sx={{ borderColor: 'rgba(16,117,186,0.08)' }} />

            <Box>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2dccd2', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
                Form Fields ({allFields.length})
              </Typography>
              {isMultiStep ? (
                task.steps.map((step) => (
                  <Box key={step.key} sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f2137', mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '6px', background: alpha('#2dccd2', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FiberManualRecord sx={{ fontSize: 8, color: '#2dccd2' }} />
                      </Box>
                      {step.label}
                    </Typography>
                    <Stack spacing={0.75} sx={{ pl: 1.5 }}>
                      {(step.fields || []).map((f) => <FieldRow key={f.key} field={f} />)}
                    </Stack>
                  </Box>
                ))
              ) : (
                <Stack spacing={0.75}>
                  {allFields.map((f) => <FieldRow key={f.key} field={f} />)}
                </Stack>
              )}
              {allFields.length === 0 && (
                <Typography sx={{ color: '#9aacb8', fontSize: '0.8rem', fontStyle: 'italic' }}>No fields defined</Typography>
              )}
            </Box>
          </Stack>
        </Box>

        {/* Footer actions */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid rgba(16,117,186,0.08)', display: 'flex', gap: 1.5, flexShrink: 0, background: '#fff' }}>
          <Button
            variant="outlined" startIcon={<Delete />} onClick={() => onDelete(task)}
            sx={{ borderRadius: '10px', borderColor: '#fca5a5', color: '#ef4444', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#fff5f5', borderColor: '#ef4444' } }}>
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained" startIcon={<Edit />} onClick={() => onEdit(task)}
            sx={{ borderRadius: '10px', background: '#2dccd2', textTransform: 'none', fontWeight: 700, boxShadow: '0 2px 8px rgba(16,117,186,0.3)', '&:hover': { background: '#0d64a3' } }}>
            Edit Task
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ field }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 1.5, py: 0.9,
      borderRadius: '8px',
      background: '#f8fafc',
      border: '1px solid rgba(16,117,186,0.07)',
    }}>
      <FieldTypeIcon type={field.type} size={14} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f2137' }}>{field.label}</Typography>
          {field.required && (
            <Box sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#d32f2f', background: '#ffebee', border: '1px solid #ffcdd2', px: 0.5, py: 0.1, borderRadius: '4px' }}>
              required
            </Box>
          )}
        </Stack>
        <Typography sx={{ fontSize: '0.68rem', color: '#9aacb8', fontFamily: 'monospace' }}>{field.key}</Typography>
      </Box>
      <Box sx={{ fontSize: '0.68rem', color: '#4a6a85', background: '#f0f4f8', border: '1px solid rgba(16,117,186,0.1)', px: 0.75, py: 0.2, borderRadius: '5px', whiteSpace: 'nowrap' }}>
        {field.type}
        {field.type === 'images' && field.maxImages ? ` ×${field.maxImages}` : ''}
        {field.type === 'select' && field.options?.length ? ` (${field.options.length})` : ''}
      </Box>
    </Box>
  );
}

// ── Task card ─────────────────────────────────────────────────────────────────
function TaskCard({ task, onClick, onEdit, onDelete }) {
  const fieldCount = countFields(task);
  const isMultiStep = !!task.steps?.length;

  return (
    <Card onClick={() => onClick(task)} sx={{
      cursor: 'pointer', transition: 'all 0.18s', position: 'relative', overflow: 'hidden',
      border: `1px solid ${task.active ? 'rgba(16,117,186,0.12)' : 'rgba(0,0,0,0.07)'}`,
      boxShadow: '0 2px 8px rgba(15,33,55,0.06)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 28px ${alpha(task.color || '#2dccd2', 0.14)}`,
        border: `1px solid ${alpha(task.color || '#2dccd2', 0.3)}`,
        '& .card-actions': { opacity: 1 },
      },
      opacity: task.active ? 1 : 0.62,
    }}>
      {/* Colour accent bar */}
      <Box sx={{ height: 3, background: task.active ? (task.color || '#2dccd2') : '#ccc', borderRadius: '3px 3px 0 0' }} />

      {/* Action buttons — appear on hover */}
      <Box className="card-actions" onClick={(e) => e.stopPropagation()}
        sx={{ position: 'absolute', top: 10, right: 8, opacity: 0, transition: 'opacity 0.15s', zIndex: 2, display: 'flex', gap: 0.5 }}>
        <Tooltip title="Edit">
          <Box onClick={(e) => { e.stopPropagation(); onEdit(task); }}
            sx={{ width: 26, height: 26, borderRadius: '7px', background: '#fff', border: '1px solid rgba(16,117,186,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(15,33,55,0.1)', cursor: 'pointer', '&:hover': { background: '#e6f2ff' } }}>
            <Edit sx={{ fontSize: 14, color: '#2dccd2' }} />
          </Box>
        </Tooltip>
        <Tooltip title="Delete">
          <Box onClick={(e) => { e.stopPropagation(); onDelete(task); }}
            sx={{ width: 26, height: 26, borderRadius: '7px', background: '#fff', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(15,33,55,0.1)', cursor: 'pointer', '&:hover': { background: '#fff5f5' } }}>
            <Delete sx={{ fontSize: 14, color: '#ef4444' }} />
          </Box>
        </Tooltip>
      </Box>

      <CardContent sx={{ p: 2, pb: '14px !important' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
            background: task.bg || '#e6f2ff',
            border: `1.5px solid ${alpha(task.color || '#2dccd2', 0.2)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.4rem', lineHeight: 1,
          }}>
            {task.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f2137', lineHeight: 1.2, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task.label}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#9aacb8', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task.id}
            </Typography>
          </Box>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', mt: 0.5, flexShrink: 0,
            background: task.active ? '#0a9e74' : '#bdbdbd',
            boxShadow: task.active ? '0 0 0 3px rgba(10,158,116,0.18)' : 'none',
          }} />
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1.5 }}>
          {isMultiStep && (
            <Chip size="small" label={`${task.steps.length} steps`}
              sx={{ height: 18, fontSize: '0.62rem', background: '#e6f2ff', color: '#2dccd2', border: '1px solid #99ccf5' }} />
          )}
          <Chip size="small" label={`${fieldCount} fields`}
            sx={{ height: 18, fontSize: '0.62rem', background: '#f5f8fc', color: '#4a6a85', border: '1px solid rgba(16,117,186,0.1)' }} />
          {task.getLocation && (
            <Chip size="small" icon={<LocationOn sx={{ fontSize: '9px !important' }} />} label="GPS"
              sx={{ height: 18, fontSize: '0.62rem', background: '#f5e6ff', color: '#7b1fa2', border: '1px solid #d9b3f5' }} />
          )}
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={0.5}>
            {(task.fields || task.steps?.flatMap((s) => s.fields) || [])
              .slice(0, 5)
              .map((f, i) => (
                <Tooltip key={i} title={`${f.label} (${f.type})`}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FieldTypeIcon type={f.type} size={12} />
                  </Box>
                </Tooltip>
              ))}
            {fieldCount > 5 && (
              <Typography sx={{ fontSize: '0.62rem', color: '#9aacb8' }}>+{fieldCount - 5}</Typography>
            )}
          </Stack>
          <Typography sx={{ fontSize: '0.62rem', color: '#c8d4dc' }}>
            #{task.sortOrder ?? 0}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Project workspace selector ────────────────────────────────────────────────
function ProjectSelector({ projects, activeId, onChange, loading }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Box onClick={() => onChange(null)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          px: 1.5, py: 0.75, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
          background: !activeId ? '#2dccd2' : '#f5f8fc',
          border: `1px solid ${!activeId ? '#2dccd2' : 'rgba(16,117,186,0.15)'}`,
          boxShadow: !activeId ? '0 2px 8px rgba(16,117,186,0.25)' : 'none',
        }}>
        <Public sx={{ fontSize: 14, color: !activeId ? '#fff' : '#4a6a85' }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: !activeId ? '#fff' : '#4a6a85' }}>All Projects</Typography>
      </Box>
      {loading
        ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rounded" width={90} height={34} sx={{ borderRadius: '10px' }} />)
        : projects.map((p) => (
          <Box key={p._id} onClick={() => onChange(p._id)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.5, py: 0.75, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
              background: activeId === p._id ? '#2dccd2' : '#f5f8fc',
              border: `1px solid ${activeId === p._id ? '#2dccd2' : 'rgba(16,117,186,0.15)'}`,
              boxShadow: activeId === p._id ? '0 2px 8px rgba(16,117,186,0.25)' : 'none',
              '&:hover': activeId !== p._id ? { background: '#eef3f9', border: '1px solid rgba(16,117,186,0.25)' } : {},
            }}>
            <Folder sx={{ fontSize: 14, color: activeId === p._id ? '#fff' : '#4a6a85' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: activeId === p._id ? '#fff' : '#4a6a85' }}>
              {p.title} {p.code ? `(${p.code})` : ''}
            </Typography>
          </Box>
        ))}
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TaskDefinitionsPage() {
  const token    = useSelector(selectToken);
  const navigate = useNavigate();

  const [tasks,         setTasks]         = useState([]);
  const [total,         setTotal]         = useState(0);
  const [totalPages,    setTotalPages]    = useState(1);
  const [page,          setPage]          = useState(1);
  const [loading,       setLoading]       = useState(false);

  const [projects,      setProjects]      = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [activeProject, setActiveProject] = useState(null);

  const [search,        setSearch]        = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewMode,      setViewMode]      = useState('card');
  const [activeFilter,  setActiveFilter]  = useState('all');

  const [selectedTask,  setSelectedTask]  = useState(null);
  const [modalOpen,     setModalOpen]     = useState(false);

  // Delete state
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleteOpen,    setDeleteOpen]    = useState(false);
  const [deleting,      setDeleting]      = useState(false);

  const searchTimer = useRef(null);

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(val); setPage(1); }, 350);
  };

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/project/`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : (data.rows || data.data || []));
    } catch (e) { console.error('fetch projects', e); }
    setProjectsLoading(false);
  }, [token]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 24 });
      if (activeProject)    params.set('projectId', activeProject);
      if (debouncedSearch)  params.set('search',    debouncedSearch);
      if (activeFilter !== 'all') params.set('active', activeFilter);
      const res  = await fetch(`${API_BASE}/admin/taskdefinitions?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const rows = data.rows || data.data || data.taskDefinitions || (Array.isArray(data) ? data : []);
      setTasks(rows);
      setTotal(data.total || rows.length);
      setTotalPages(data.totalPages || data.lastPage || Math.ceil((data.total || rows.length) / 24) || 1);
    } catch (e) { console.error('fetch tasks', e); }
    setLoading(false);
  }, [token, page, activeProject, debouncedSearch, activeFilter]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchTasks(); },    [fetchTasks]);

  const handleProjectChange = (id) => { setActiveProject(id); setPage(1); };

  // ── Navigation helpers ────────────────────────────────────────────────────
  const openTask   = (task) => { setSelectedTask(task); setModalOpen(true); };
  const editTask   = (task) => { setModalOpen(false); navigate(`/taskforms/${task._id}`); };
  const createTask = () => navigate('/taskforms/new');

  // ── Delete helpers ────────────────────────────────────────────────────────
  const requestDelete = (task) => {
    setModalOpen(false);          // close detail modal if open
    setDeleteTarget(task);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/taskdefinitions/${deleteTarget._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      setTasks((prev) => prev.filter((t) => t._id !== deleteTarget._id));
      setTotal((n) => n - 1);
    } catch (e) {
      console.error('delete failed', e);
    }
    setDeleting(false);
    setDeleteOpen(false);
    setDeleteTarget(null);
  };

  const activeCount   = tasks.filter((t) => t.active).length;
  const inactiveCount = tasks.filter((t) => !t.active).length;

  return (
        <Box sx={{ height: 'calc(94vh)', display: 'flex', flexDirection: 'column', gap: 2 }}>


      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.25 }}>
            Task Forms
          </Typography>
          <Typography variant="body2" sx={{ color: '#4a6a85' }}>
            Manage task templates and form configurations
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {[
            { label: `${total} Total`,            color: '#2dccd2', bg: '#e6f2ff', border: '#99ccf5' },
            { label: `${activeCount} Active`,     color: '#0a9e74', bg: '#e6f9f4', border: '#b3ead9' },
            { label: `${inactiveCount} Inactive`, color: '#9e9e9e', bg: '#f5f5f5', border: '#e0e0e0' },
          ].map((s) => (
            <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '10px', background: s.bg, border: `1px solid ${s.border}` }}>
              <FiberManualRecord sx={{ fontSize: 8, color: s.color }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: s.color }}>{s.label}</Typography>
            </Box>
          ))}
          <Button variant="contained" startIcon={<Add />} onClick={createTask}
            sx={{ borderRadius: '10px', px: 2, py: 0.875, fontWeight: 700, fontSize: '0.82rem', boxShadow: '0 2px 8px rgba(16,117,186,0.3)', whiteSpace: 'nowrap' }}>
            New Task
          </Button>
        </Stack>
      </Box>

      {/* ── Project workspace tabs ── */}
      <Box sx={{ background: '#fff', borderRadius: '14px', px: 2, py: 1.5, border: '1px solid rgba(16,117,186,0.1)', boxShadow: '0 2px 8px rgba(15,33,55,0.04)' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <Folder sx={{ fontSize: 14, color: '#4a6a85' }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4a6a85', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Workspace</Typography>
        </Stack>
        <ProjectSelector projects={projects} activeId={activeProject} onChange={handleProjectChange} loading={projectsLoading} />
      </Box>

      {/* ── Controls bar ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
        <TextField
          size="small" placeholder="Search tasks…" value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ color: '#4a6a85', fontSize: 18 }} /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearchChange('')}><Close sx={{ fontSize: 15 }} /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" spacing={0.5}>
          {[['all','All'],['true','Active'],['false','Inactive']].map(([val, lbl]) => (
            <Box key={val} onClick={() => { setActiveFilter(val); setPage(1); }} sx={{
              px: 1.25, py: 0.6, borderRadius: '8px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
              background: activeFilter === val ? '#2dccd2' : '#f5f8fc',
              color:      activeFilter === val ? '#fff' : '#4a6a85',
              border: `1px solid ${activeFilter === val ? '#2dccd2' : 'rgba(16,117,186,0.15)'}`,
              transition: 'all 0.15s',
              '&:hover': activeFilter !== val ? { background: '#eef3f9' } : {},
            }}>{lbl}</Box>
          ))}
        </Stack>
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
          <ToggleButton value="card" sx={{ px: 1.5 }}><GridView sx={{ fontSize: 18 }} /></ToggleButton>
          <ToggleButton value="list" sx={{ px: 1.5 }}><ViewList sx={{ fontSize: 18 }} /></ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchTasks} disabled={loading}
            sx={{ color: '#2dccd2', background: alpha('#2dccd2', 0.08), border: `1px solid ${alpha('#2dccd2', 0.2)}`, borderRadius: '10px', width: 36, height: 36 }}>
            {loading ? <CircularProgress size={16} sx={{ color: '#2dccd2' }} /> : <Refresh fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Content ── */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── CARD VIEW ── */}
        {viewMode === 'card' && (
          <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
            {loading ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
                {Array.from({ length: 12 }).map((_, i) => (
                  <Card key={i} sx={{ p: 2, border: '1px solid rgba(16,117,186,0.08)' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px', flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={16} />
                        <Skeleton variant="text" width="50%" height={13} />
                      </Box>
                    </Stack>
                    <Skeleton variant="rounded" height={22} width="60%" sx={{ borderRadius: '6px', mb: 1 }} />
                    <Skeleton variant="text" width="40%" height={13} />
                  </Card>
                ))}
              </Box>
            ) : tasks.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 8 }}>
                <Extension sx={{ fontSize: 56, color: '#c8d4dc', mb: 2 }} />
                <Typography sx={{ color: '#6b7f8e', fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>No task forms found</Typography>
                <Typography sx={{ color: '#9aacb8', fontSize: '0.82rem' }}>
                  {debouncedSearch ? 'Try a different search term' : 'Create your first task definition'}
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={createTask}
                  sx={{ mt: 2.5, borderRadius: '10px', fontWeight: 700, boxShadow: '0 2px 8px rgba(16,117,186,0.3)' }}>
                  New Task
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
                {tasks.map((task) => (
                  <TaskCard key={task._id || task.id} task={task} onClick={openTask} onEdit={editTask} onDelete={requestDelete} />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── LIST VIEW ── */}
        {viewMode === 'list' && (
          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {['Icon', 'Label', 'ID', 'Fields', 'Steps', 'Features', 'Status', ''].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', py: 1.5, background: '#f5f8fc' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}><Skeleton variant="rounded" height={16} /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8, color: '#6b7f8e' }}>
                        No task forms found
                      </TableCell>
                    </TableRow>
                  ) : tasks.map((task) => {
                    const fc = countFields(task);
                    return (
                      <TableRow key={task._id || task.id} hover
                        onClick={() => openTask(task)}
                        sx={{ cursor: 'pointer', opacity: task.active ? 1 : 0.55, '&:hover': { background: alpha('#2dccd2', 0.04) } }}>
                        <TableCell>
                          <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: task.bg || '#e6f2ff', border: `1.5px solid ${alpha(task.color || '#2dccd2', 0.22)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                            {task.icon}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f2137' }}>{task.label}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#9aacb8', fontFamily: 'monospace' }}>{task.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#0f2137', fontWeight: 600 }}>{fc}</Typography>
                        </TableCell>
                        <TableCell>
                          {task.steps?.length
                            ? <Box sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2dccd2', background: '#e6f2ff', border: '1px solid #99ccf5', px: 0.75, py: 0.2, borderRadius: '5px', display: 'inline-block' }}>{task.steps.length} steps</Box>
                            : <Typography sx={{ color: '#c8d4dc', fontSize: '0.75rem' }}>—</Typography>
                          }
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          <Stack direction="row" spacing={0.5}>
                            {task.getLocation && (
                              <Tooltip title="Captures GPS location"><LocationOn sx={{ fontSize: 15, color: '#7b1fa2' }} /></Tooltip>
                            )}
                            {(task.fields || task.steps?.flatMap((s) => s.fields) || []).some((f) => f.type === 'images') && (
                              <Tooltip title="Has image fields"><ImageIcon sx={{ fontSize: 15, color: '#c06000' }} /></Tooltip>
                            )}
                            {(task.fields || task.steps?.flatMap((s) => s.fields) || []).some((f) => f.type === 'signature') && (
                              <Tooltip title="Has signature field"><Extension sx={{ fontSize: 15, color: '#d32f2f' }} /></Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.68rem', fontWeight: 700, color: task.active ? '#0a9e74' : '#9e9e9e', background: task.active ? '#e6f9f4' : '#f5f5f5', border: `1px solid ${task.active ? '#b3ead9' : '#e0e0e0'}`, px: 0.9, py: 0.3, borderRadius: '6px' }}>
                            {task.active ? <CheckCircle sx={{ fontSize: 11 }} /> : <Cancel sx={{ fontSize: 11 }} />}
                            {task.active ? 'Active' : 'Inactive'}
                          </Box>
                        </TableCell>
                        {/* Row action buttons */}
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => editTask(task)}
                                sx={{ color: '#2dccd2', background: alpha('#2dccd2', 0.07), border: `1px solid ${alpha('#2dccd2', 0.15)}`, borderRadius: '8px', width: 28, height: 28, '&:hover': { background: alpha('#2dccd2', 0.15) } }}>
                                <Edit sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => requestDelete(task)}
                                sx={{ color: '#ef4444', background: alpha('#ef4444', 0.07), border: `1px solid ${alpha('#ef4444', 0.15)}`, borderRadius: '8px', width: 28, height: 28, '&:hover': { background: alpha('#ef4444', 0.15) } }}>
                                <Delete sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid rgba(16,117,186,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85' }}>
                Showing <strong>{tasks.length}</strong> of <strong>{total}</strong>
              </Typography>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" color="primary" />
            </Box>
          </Card>
        )}

        {viewMode === 'card' && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 0.5, flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85' }}>{tasks.length} of {total} tasks</Typography>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" color="primary" />
          </Box>
        )}
      </Box>

      {/* ── Detail modal ── */}
      <TaskDetailModal
        task={selectedTask} open={modalOpen} onClose={() => setModalOpen(false)}
        onEdit={editTask} onDelete={requestDelete}
      />

      {/* ── Delete confirm dialog ── */}
      <DeleteConfirmDialog
        task={deleteTarget} open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        onConfirm={confirmDelete} deleting={deleting}
      />
    </Box>
  );
}
